/**
 * Address field with suggestions, for the web portals.
 *
 * Presentational on purpose: the lookups arrive as props rather than being
 * imported. `libs/ui` has no business knowing which API a portal talks to, and
 * each app already owns its own axios client — so gov, web and admin can each
 * pass their own fetchers and share this one field.
 *
 * The mobile app has its own copy of this against the same endpoints; the
 * shared parts (session semantics, thresholds) live in the API contract, not in
 * a component either platform could import.
 *
 * It degrades to a plain `TextField`: with no suggestions available — no key
 * configured, request failed, offline — typing the address by hand works
 * exactly as before. Suggestions only ever save keystrokes.
 */
import React, { useEffect, useMemo, useRef, useState, type Ref } from 'react';
import { Autocomplete, Box, TextField, Typography } from '../primitives';
import * as Icons from '../icons';
import { iconSize } from '@munifocus/tokens';
import { dedupeSuggestions, withTypedCivicNumber } from '@munifocus/utils';

export interface AddressSuggestion {
  placeId: string;
  description: string;
  primary: string;
  secondary: string;
}

export interface AddressDetail {
  placeId: string;
  address: string;
  city: string;
  province: string | null;
  postalCode: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface AddressAutocompleteProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Fired when a suggestion is resolved. */
  onPick: (place: AddressDetail) => void;
  /** Suggestions for a partial address, within one billing session. */
  fetchSuggestions: (query: string, sessionToken: string) => Promise<AddressSuggestion[]>;
  /** Resolve a pick. Must be given the same token that produced it. */
  fetchDetail: (placeId: string, sessionToken: string) => Promise<AddressDetail | null>;
  /** Mint a session id. One per address entry. */
  newSessionToken: () => string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  /** The text input, for a form that moves focus to its first invalid field. */
  inputRef?: Ref<HTMLInputElement>;
  size?: 'small' | 'medium';
  /** Shown under the list; Google requires visible attribution. */
  attribution?: string;
  /** Shown in the list while a lookup is in flight. */
  loadingText?: string;
}

/** Below this a query is too vague to be worth a billed request. */
const MIN_QUERY_LENGTH = 3;
/** How long typing must pause before spending one. */
const DEBOUNCE_MS = 250;

// The pick rules live in `@munifocus/utils` so the mobile field applies the
// same ones; re-exported here for the web callers and specs that use them.
export { dedupeSuggestions, withTypedCivicNumber } from '@munifocus/utils';

export function AddressAutocomplete({
  label,
  value,
  onChange,
  onPick,
  fetchSuggestions,
  fetchDetail,
  newSessionToken,
  required,
  disabled,
  error,
  helperText,
  inputRef,
  size = 'small',
  attribution,
  loadingText,
}: AddressAutocompleteProps) {
  const [options, setOptions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const session = useRef<string>(newSessionToken());
  const requestId = useRef(0);
  // The latest fetcher, read when the debounce fires rather than listed as a
  // dependency. Callers pass an inline arrow (`(q, token) => fetch(q, token,
  // locale)`), so its identity changes on every render of the *form* — and as a
  // dependency it re-ran this effect on every keystroke in any other field.
  // Each re-run set state from a passive effect, leaving a default-priority
  // render pending behind the keystroke's synchronous one; a burst of typing
  // (fast typists, autofill, an automated walk) stacked fifty of those and
  // React aborted the next update with "Maximum update depth exceeded" — the
  // keystroke in the description box was the update it threw away. It also
  // restarted the billed suggestion request on every character typed elsewhere.
  const fetchRef = useRef(fetchSuggestions);
  useEffect(() => {
    fetchRef.current = fetchSuggestions;
  }, [fetchSuggestions]);

  // Mirrors of the two pieces of state, so the effect below can tell whether an
  // update would change anything before making it. It runs on every keystroke
  // in this field, and a state update from a passive effect on every keystroke
  // is exactly what a fast burst of typing stacks into React #185 and a
  // dropped character — even a same-value one, which React cannot always
  // short-circuit. So: set nothing while typing, and set loading only when the
  // debounced request actually goes out.
  const optionsEmpty = useRef(true);
  optionsEmpty.current = options.length === 0;
  const loadingNow = useRef(false);
  loadingNow.current = loading;

  useEffect(() => {
    const q = value.trim();
    if (disabled || q.length < MIN_QUERY_LENGTH) {
      requestId.current++;
      if (!optionsEmpty.current) setOptions([]);
      if (loadingNow.current) setLoading(false);
      return;
    }

    const id = ++requestId.current;
    const timer = setTimeout(() => {
      setLoading(true);
      fetchRef
        .current(q, session.current)
        .then((next) => {
          // Typing outruns the network: without this an earlier, slower
          // response can land last and replace the list for what was typed.
          if (id === requestId.current) setOptions(dedupeSuggestions(next));
        })
        .catch(() => {
          if (id === requestId.current) setOptions([]);
        })
        .finally(() => {
          if (id === requestId.current) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [value, disabled]);

  const pick = async (suggestion: AddressSuggestion, typed: string) => {
    const token = session.current;
    // The details call closes this billing session; anything typed afterwards
    // belongs to a new one.
    session.current = newSessionToken();
    requestId.current++;
    setOptions([]);
    const place = await fetchDetail(suggestion.placeId, token).catch(() => null);
    if (place) onPick(withTypedCivicNumber(typed, place));
  };

  // MUI filters options against the input by default, which would hide
  // suggestions Google already decided were relevant.
  const passthrough = useMemo(() => (opts: AddressSuggestion[]) => opts, []);

  return (
    <Autocomplete<AddressSuggestion, false, false, true>
      freeSolo
      disabled={disabled}
      options={options}
      filterOptions={passthrough}
      inputValue={value}
      onInputChange={(_, next, reason) => {
        // Not on a pick either: MUI would write the option's label ("Elm
        // Street") over what was typed ("45 Elm Street"). The text stays as
        // typed until `onPick` hands the caller the resolved place — and if
        // that lookup fails, what the person typed is still there.
        if (reason !== 'reset' && reason !== 'selectOption') onChange(next);
      }}
      onChange={(_, selected) => {
        if (selected && typeof selected !== 'string') void pick(selected, value);
      }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.primary)}
      isOptionEqualToValue={(a, b) =>
        typeof a !== 'string' && typeof b !== 'string' && a.placeId === b.placeId
      }
      noOptionsText={attribution}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.placeId} sx={{ gap: 1 }}>
          <Icons.MapPin size={iconSize.sm} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {option.primary}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {option.secondary}
            </Typography>
          </Box>
        </Box>
      )}
      // `loading` rather than a hand-built endAdornment: passing our own
      // `slotProps` to this TextField replaces the object `params` already
      // carries, which is where the Autocomplete keeps its input wiring —
      // dropping it silently stops typing from reaching the form at all.
      loading={loading}
      loadingText={loadingText}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          inputRef={inputRef}
          helperText={helperText ?? (options.length > 0 ? attribution : undefined)}
          size={size}
        />
      )}
    />
  );
}
